import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { fetchComparables, fetchGraphVenues, type ComparableArtist, type GraphVenue } from "@/api/discover";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useArtistScope } from "@/lib/artistContext";
import ComparableArtistsPanel from "./ComparableArtistsPanel";
import DiscoverVenuesPanel from "./DiscoverVenuesPanel";
import { friendlyError } from "./discoverCopy";

class VenuePaginationError extends Error {}

type TabId = "artists" | "venues";

const PAGE_DESCRIPTION = "Explore artists and venues to find your next opportunity.";

/** `?tab=venues` / `?tab=artists` deep-links a tab (e.g. Home → Explore venues). */
function parseTab(value: string | null): TabId | null {
  return value === "venues" || value === "artists" ? value : null;
}

/**
 * Discover: comparable artists → where they've played → venues worth saving.
 *
 * Page loads only read stored data (comparables + graph venues). Nothing here
 * contacts an external provider. Selection records shared hydration intent;
 * this page passively observes accepted evidence and request status.
 */
export default function DiscoverPage() {
  const { selectedArtistId } = useArtistScope();
  const artistId = selectedArtistId || null;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = parseTab(searchParams.get("tab"));
  const [tab, setTab] = useState<TabId | null>(null);
  const changeTab = useCallback(
    (next: TabId) => {
      setTab(next);
      // Keep the choice in the URL so refresh and Back restore the same tab.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [comparables, setComparables] = useState<ComparableArtist[]>([]);
  const [comparablesLoading, setComparablesLoading] = useState(false);
  const [comparablesError, setComparablesError] = useState<string | null>(null);
  const [venues, setVenues] = useState<GraphVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [venuesError, setVenuesError] = useState<string | null>(null);
  const [venueLoaded, setVenueLoaded] = useState(0);
  const [venueTotal, setVenueTotal] = useState<number | null>(null);
  const [venuesComplete, setVenuesComplete] = useState(false);
  const venueGeneration = useRef(0);
  const venueInFlight = useRef(false);
  const venueResume = useRef<{ artist: string; offset: number; snapshot?: string; total: number; rows: GraphVenue[] } | null>(null);
  const resetArtistRef = useRef<string | null>(null);
  const [eventScope, setEventScope] = useState<"all" | "past" | "upcoming">("all");
  const [artistFilter, setArtistFilter] = useState<{ id: number; name: string } | null>(null);
  const [comparablesLoadedFor, setComparablesLoadedFor] = useState<string | null>(null);
  const [venuesLoadedFor, setVenuesLoadedFor] = useState<string | null>(null);

  // Ignore responses that arrive after the user switched artists.
  const currentArtistRef = useRef<string | null>(artistId);
  currentArtistRef.current = artistId;

  const loadComparables = useCallback(async (background = false) => {
    if (!artistId) return;
    const requested = artistId;
    if (!background) setComparablesLoading(true);
    setComparablesError(null);
    try {
      const payload = await fetchComparables(requested);
      if (currentArtistRef.current !== requested) return;
      setComparables(payload.comparables ?? []);
    } catch (err) {
      if (currentArtistRef.current !== requested) return;
      setComparablesError(friendlyError(err, "We couldn't load your comparable artists. Please try again."));
    } finally {
      if (!background && currentArtistRef.current === requested) {
        setComparablesLoading(false);
        setComparablesLoadedFor(requested);
      }
    }
  }, [artistId]);

  const loadVenues = useCallback(async (background = false, resume = false) => {
    if (!artistId || (background && venueInFlight.current)) return;
    const requested = artistId;
    const generation = ++venueGeneration.current;
    const current = () => currentArtistRef.current === requested && venueGeneration.current === generation;
    venueInFlight.current = true;
    setVenuesLoading(true);
    setVenuesError(null);
    setVenuesComplete(false);
    const previous = resume && venueResume.current?.artist === requested ? venueResume.current : null;
    if (!previous) {
      venueResume.current = null;
      setVenueTotal(null);
    }
    let offset = previous?.offset ?? 0;
    let snapshot = previous?.snapshot;
    let total: number | null = previous?.total ?? null;
    const collected = new Map<string, GraphVenue>();
    const keyOf = (v: GraphVenue) => v.global_venue_id ? `global:${v.global_venue_id}` : `venue:${v.venue_ids.join("-")}`;
    for (const venue of previous?.rows ?? []) collected.set(keyOf(venue), venue);
    if (!background && !previous) {
      setVenues([]);
    }
    setVenueLoaded(collected.size);
    try {
      while (true) {
        const payload = await fetchGraphVenues(requested, 500, offset, snapshot, eventScope);
        // Logical cancellation also stops subsequent page requests after scope changes.
        if (!current()) return;
        if (total !== null && total !== payload.venue_count) throw new VenuePaginationError("Venue results changed while loading. Reload venues.");
        if (snapshot && payload.snapshot !== snapshot) throw new VenuePaginationError("Venue results changed while loading. Reload venues.");
        total = payload.venue_count;
        snapshot = payload.snapshot;
        setVenueTotal(total);
        for (const venue of payload.venues ?? []) {
          const key = keyOf(venue);
          if (!collected.has(key)) collected.set(key, venue);
        }
        const next = payload.next_offset === undefined
          ? (offset + payload.venues.length < total ? offset + payload.venues.length : null)
          : payload.next_offset;
        const rows = [...collected.values()];
        setVenueLoaded(rows.length);
        if (!background) setVenues(rows);
        if (next === null) {
          if (rows.length !== total) throw new VenuePaginationError("Venue results are incomplete. Reload venues to try again.");
          setVenues(rows);
          setVenuesComplete(true);
          venueResume.current = null;
          break;
        }
        if (next <= offset || next > total || !payload.venues.length) throw new VenuePaginationError("Venue pagination stopped before all records loaded.");
        venueResume.current = { artist: requested, offset: next, snapshot, total, rows };
        offset = next;
      }
    } catch (err) {
      if (!current()) return;
      const graphChanged = (err as { payload?: { detail?: { error?: string } } })?.payload?.detail?.error === "venue_graph_changed";
      if (err instanceof VenuePaginationError || graphChanged) venueResume.current = null;
      setVenuesError(err instanceof VenuePaginationError ? err.message : graphChanged
        ? "Venue results changed while loading. Reload venues to get complete results."
        : friendlyError(err, "We couldn't load all venues. Please try again."));
    } finally {
      if (current()) {
        venueInFlight.current = false;
        setVenuesLoading(false);
        setVenuesLoadedFor(requested);
      }
    }
  }, [artistId, eventScope]);

  const comparablesChanged = useCallback(async () => {
    // Comparable panel invokes onEvidenceUpdated after this callback completes.
    ++venueGeneration.current;
    venueInFlight.current = false;
    venueResume.current = null;
    setVenues([]);
    setVenueTotal(null);
    setVenuesComplete(false);
    await loadComparables();
  }, [loadComparables]);

  useEffect(() => {
    // Temporal view changes reload evidence without resetting the selected tab.
    const artistChanged = resetArtistRef.current !== artistId;
    resetArtistRef.current = artistId;
    if (artistChanged) setComparables([]);
    setVenues([]);
    setVenueTotal(null);
    setVenuesComplete(false);
    venueResume.current = null;
    venueInFlight.current = false;
    if (artistChanged) { setArtistFilter(null); setTab(null); }
    if (!artistId) return;
    if (artistChanged) void loadComparables();
    void loadVenues();
    return () => { ++venueGeneration.current; venueInFlight.current = false; };
  }, [artistId, loadComparables, loadVenues]);

  useEffect(() => {
    const pending = comparables.some(c => c.hydration && ["requested", "commissioned", "running", "review_required", "deferred"].includes(c.hydration.state));
    if (!artistId || !pending) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void loadComparables(true);
      void loadVenues(true);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [artistId, comparables, loadComparables, loadVenues]);

  // First-use default: start on Artists until there is something to show on Venues.
  // An explicit ?tab= always wins over the default.
  useEffect(() => {
    if (tab !== null || urlTab !== null || !artistId) return;
    if (comparablesLoadedFor !== artistId || venuesLoadedFor !== artistId) return;
    setTab(comparables.length > 0 && venues.length > 0 ? "venues" : "artists");
  }, [tab, urlTab, comparablesLoadedFor, venuesLoadedFor, comparables.length, venues.length, artistId]);

  const handleVenuePrivateState = (globalVenueId: string, privateState: GraphVenue["private_state"]) => {
    if (currentArtistRef.current !== artistId) return;
    // A committed action changes the server snapshot; do not resume older pages.
    venueResume.current = null;
    if (venueInFlight.current) {
      venueInFlight.current = false;
      void loadVenues(true);
    }
    setVenues((prev) =>
      prev.map((venue) => (venue.global_venue_id === globalVenueId ? { ...venue, private_state: privateState } : venue)),
    );
  };

  /** Pitch created/reused the Booking record (and may have marked contact). Save state is untouched. */
  const handleVenuePitchState = (globalVenueId: string, state: "prospect" | "contacted") => {
    if (currentArtistRef.current !== artistId) return;
    venueResume.current = null;
    setVenues((prev) =>
      prev.map((venue) =>
        venue.global_venue_id === globalVenueId
          ? {
              ...venue,
              private_state: {
                ...venue.private_state,
                has_relationship: true,
                status: state,
                in_booking: true,
                dismissed: false,
                dismissed_at: null,
                contacted_at:
                  state === "contacted" ? venue.private_state.contacted_at ?? new Date().toISOString() : venue.private_state.contacted_at ?? null,
              },
            }
          : venue,
      ),
    );
    // Quietly pick up server truth (e.g. the Booking link) without a loading state.
    void loadVenues(true);
  };

  if (!artistId) {
    return (
      <div className="aa-page-shell space-y-4">
        <PageHeader title="Discover" description={PAGE_DESCRIPTION} />
        <div className="aa-glass-card rounded-2xl p-6 text-center text-sm text-muted-foreground">
          Select an artist to start discovering venues.
        </div>
      </div>
    );
  }

  return (
    <div className="aa-page-shell space-y-4">
      <PageHeader title="Discover" description={PAGE_DESCRIPTION} />
      <Tabs value={tab ?? urlTab ?? "artists"} onValueChange={(value) => changeTab(value as TabId)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="artists">Artists</TabsTrigger>
          <TabsTrigger value="venues">Venues</TabsTrigger>
        </TabsList>
        <TabsContent value="artists">
          <ComparableArtistsPanel
            artistId={artistId}
            comparables={comparables}
            loading={comparablesLoading}
            error={comparablesError}
            onChanged={comparablesChanged}
            onEvidenceUpdated={() => void loadVenues()}
            onViewVenues={(comparable) => {
              setArtistFilter({ id: comparable.public_artist.id, name: comparable.public_artist.name });
              changeTab("venues");
            }}
          />
        </TabsContent>
        <TabsContent value="venues">
          <label className="mb-3 flex items-center gap-2 text-sm">
            Performance evidence
            <select aria-label="Performance evidence" value={eventScope} onChange={(event) => setEventScope(event.target.value as "all" | "past" | "upcoming")} className="rounded border bg-background px-2 py-1">
              <option value="all">All</option><option value="past">Past</option><option value="upcoming">Upcoming</option>
            </select>
          </label>
          <DiscoverVenuesPanel
            key={artistId}
            artistId={artistId}
            venues={venues}
            comparableCount={comparables.length}
            loading={venuesLoading}
            error={venuesError}
            total={venueTotal}
            loadedCount={venueLoaded}
            complete={venuesComplete}
            onRetry={() => void loadVenues(false, true)}
            onReload={() => { venueResume.current = null; void loadVenues(); }}
            artistFilter={artistFilter}
            onClearArtistFilter={() => setArtistFilter(null)}
            onVenuePrivateState={handleVenuePrivateState}
            onVenuePitchState={handleVenuePitchState}
            onGoToArtists={() => changeTab("artists")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
